import {
  capPropertyAssets,
  createAdapterRegistry,
  type ExtractedAsset,
  extractGenericAssets,
  toPortableAssets,
} from "@repo/adapter-core";
import { ALLHOMES_ADAPTER } from "./allhomes-com-au/index.ts";
import { BARRY_PLANT_ADAPTER } from "./barry-plant/index.ts";
import { BELLE_PROPERTY_ADAPTER } from "./belle-property/index.ts";
import { BUXTON_ADAPTER } from "./buxton/index.ts";
import { DOMAIN_ADAPTER } from "./domain-com-au/index.ts";
import { JELLIS_CRAIG_ADAPTER } from "./jellis-craig/index.ts";
import { KAY_BURTON_BAYSIDE_ADAPTER } from "./kay-burton-bayside/index.ts";
import { LUTON_PROPERTIES_ADAPTER } from "./luton-properties/index.ts";
import { MARSHALL_WHITE_ADAPTER } from "./marshall-white/index.ts";
import { MCGRATH_ADAPTER } from "./mcgrath/index.ts";
import { PETRUSMA_PROPERTY_ADAPTER } from "./petrusma-property/index.ts";
import { PLACE_ESTATE_AGENTS_ADAPTER } from "./place-estate-agents/index.ts";
import { PROPERTY_COM_AU_ADAPTER } from "./property-com-au/index.ts";
import { RAY_WHITE_ADAPTER } from "./ray-white/index.ts";
import { REALESTATE_ADAPTER } from "./realestate-com-au/index.ts";
import { REALMARK_ADAPTER } from "./realmark/index.ts";
import { TOOP_AND_TOOP_ADAPTER } from "./toop-and-toop/index.ts";

export { ALLHOMES_ADAPTER } from "./allhomes-com-au/index.ts";
export { BARRY_PLANT_ADAPTER } from "./barry-plant/index.ts";
export { BELLE_PROPERTY_ADAPTER } from "./belle-property/index.ts";
export { BUXTON_ADAPTER } from "./buxton/index.ts";
export { DOMAIN_ADAPTER } from "./domain-com-au/index.ts";
export { JELLIS_CRAIG_ADAPTER } from "./jellis-craig/index.ts";
export { KAY_BURTON_BAYSIDE_ADAPTER } from "./kay-burton-bayside/index.ts";
export { LUTON_PROPERTIES_ADAPTER } from "./luton-properties/index.ts";
export { MARSHALL_WHITE_ADAPTER } from "./marshall-white/index.ts";
export { MCGRATH_ADAPTER } from "./mcgrath/index.ts";
export { PETRUSMA_PROPERTY_ADAPTER } from "./petrusma-property/index.ts";
export { PLACE_ESTATE_AGENTS_ADAPTER } from "./place-estate-agents/index.ts";
export { PROPERTY_COM_AU_ADAPTER } from "./property-com-au/index.ts";
export { RAY_WHITE_ADAPTER } from "./ray-white/index.ts";
export { REALESTATE_ADAPTER } from "./realestate-com-au/index.ts";
export { REALMARK_ADAPTER } from "./realmark/index.ts";
export { mergeAdapterParserData, mergeAdapterParserDataWithSources } from "./shared/merge-parser-data.ts";
export { TOOP_AND_TOOP_ADAPTER } from "./toop-and-toop/index.ts";

export const ADAPTER_DEFINITIONS = [
  DOMAIN_ADAPTER,
  REALESTATE_ADAPTER,
  ALLHOMES_ADAPTER,
  PROPERTY_COM_AU_ADAPTER,
  RAY_WHITE_ADAPTER,
  MCGRATH_ADAPTER,
  BELLE_PROPERTY_ADAPTER,
  BARRY_PLANT_ADAPTER,
  JELLIS_CRAIG_ADAPTER,
  PLACE_ESTATE_AGENTS_ADAPTER,
  LUTON_PROPERTIES_ADAPTER,
  REALMARK_ADAPTER,
  TOOP_AND_TOOP_ADAPTER,
  PETRUSMA_PROPERTY_ADAPTER,
  MARSHALL_WHITE_ADAPTER,
  KAY_BURTON_BAYSIDE_ADAPTER,
  BUXTON_ADAPTER,
] as const;

export const adapterRegistry = createAdapterRegistry(ADAPTER_DEFINITIONS);

export const getAdapter = adapterRegistry.getAdapter;
export const getAllAdapters = adapterRegistry.getAllAdapters;
export const hasAdapter = adapterRegistry.hasAdapter;
export const findAdapterForUrl = adapterRegistry.findAdapterForUrl;
export const getDefaultAdapterConfig = adapterRegistry.getDefaultConfig;
export const buildSearchUrlForAdapter = adapterRegistry.buildSearchUrl;
export const buildPaginatedUrlForAdapter = adapterRegistry.buildPaginatedUrl;

/**
 * Resolve the adapter's site-specific HTML parser for a given host. Returns
 * undefined when the adapter doesn't define one — callers should fall back
 * to the generic parser in `@repo/scrape-providers`.
 */
export function getAdapterHtmlParserForUrl(
  url: string,
): ((html: string, url: string) => Record<string, unknown>) | undefined {
  return findAdapterForUrl(url)?.parseDetailHtml;
}

/**
 * Resolve the adapter's bot-detection signature check for a given host.
 * Returns undefined when the adapter doesn't define one.
 */
export function getAdapterBotDetectorForUrl(
  url: string,
): ((data: Record<string, unknown>) => boolean) | undefined {
  return findAdapterForUrl(url)?.detectBotResponse;
}

/**
 * True when the adapter's search pages go to Bright Data first. Look up by
 * slug, never by URL: `meta.urlPattern` matches detail pages only.
 */
export function isAdapterBrightdataDiscoveryFirst(slug: string): boolean {
  return getAdapter(slug)?.brightdataDiscoveryFirst === true;
}

/** True when the adapter's site refuses Zyte; see AdapterDefinition.zyteBlocked. */
export function isAdapterZyteBlocked(slug: string): boolean {
  return getAdapter(slug)?.zyteBlocked === true;
}

/** True when the adapter's pages try a free direct fetch first; see AdapterDefinition.directFirst. */
export function isAdapterDirectFirst(slug: string): boolean {
  return getAdapter(slug)?.directFirst === true;
}

/** Direct-fetch timeout for the adapter's pages; see AdapterDefinition.directTimeoutMs. */
export function getAdapterDirectTimeoutMs(slug: string): number {
  return getAdapter(slug)?.directTimeoutMs ?? 30_000;
}

/** True when seat overflow may go to Bright Data; see AdapterDefinition.brightdataOverflow. */
export function isAdapterBrightdataOverflow(slug: string): boolean {
  return getAdapter(slug)?.brightdataOverflow === true;
}

/**
 * Env var holding the one Web Unlocker zone (`bd_webunlocker_realestate_es`).
 * Unlocker zones are service plans, not host-locked, so it serves any host.
 */
export const BRIGHTDATA_UNLOCKER_ZONE_ENV_VAR = "BRIGHTDATA_REA_COLLECTOR";

/** The Web Unlocker zone every adapter uses, for discovery-first pages and for block escalation. */
export function getBrightdataUnlockerZone(): string | undefined {
  return readEnvVar(BRIGHTDATA_UNLOCKER_ZONE_ENV_VAR);
}

/**
 * Resolve the Bright Data Scraping Browser WSS URL (Playwright CDP target)
 * for an adapter. Adapters opt in by setting `brightdataBrowserZoneEnvVar`
 * to the env-var name holding the URL (eg. `BRIGHTDATA_SPA_BROWSER_WSS_URL`).
 * Use this when the adapter's listings hydrate client-side — McGrath
 * catch-all routes, property.com.au.
 */
export function getAdapterBrightdataBrowserWssBySlug(slug: string): string | undefined {
  const adapter = getAdapter(slug);
  if (!adapter?.brightdataBrowserZoneEnvVar) return undefined;
  return readEnvVar(adapter.brightdataBrowserZoneEnvVar);
}

/**
 * Read an env var across Node + Deno + browser bundles. Adapter-definitions
 * is consumed by the Vite browser build, Firebase Node workers, AND Supabase
 * Deno edge functions — `process.env` is only populated in Node; Deno
 * requires `Deno.env.get()`. The browser bundle never invokes this code path
 * but TypeScript still type-checks every branch, so both globals are
 * guarded behind a `typeof` check.
 */
function readEnvVar(name: string): string | undefined {
  const denoEnv = (globalThis as { Deno?: { env?: { get?: (k: string) => string | undefined } } })
    .Deno;
  if (denoEnv?.env?.get) {
    const value = denoEnv.env.get(name);
    if (value && value.length > 0) return value;
  }
  const nodeEnv = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process
    ?.env;
  const value = nodeEnv?.[name];
  return value && value.length > 0 ? value : undefined;
}

/**
 * Resolve the asset extractor for a given URL. Returns the adapter's
 * site-specific extractor when defined; otherwise falls through to
 * `extractGenericAssets`. Always returns a callable — callers don't
 * need to null-check.
 */
export function getAdapterAssetExtractorForUrl(
  url: string,
): (html: string, baseUrl: string) => ExtractedAsset[] {
  const adapter = findAdapterForUrl(url);
  return adapter?.extractAssets ?? extractGenericAssets;
}

/**
 * The assets worth downloading for a detail page: the adapter's extractor (or the generic one),
 * capped per type and stripped of undefined fields. Never throws; a broken extractor yields [].
 */
export function selectPropertyAssetsForUrl(url: string, html: string | null): ExtractedAsset[] {
  if (!html) return [];
  try {
    return toPortableAssets(capPropertyAssets(getAdapterAssetExtractorForUrl(url)(html, url)));
  } catch {
    return [];
  }
}
